// Drive-Thru Demo API (Tealive / Bask Bear POS-screen workspace).
// BE: feature/drive-thru-agent-stg — app/interface/routers/drive_thru_demo.py.
// Every endpoint requires the normal JWT Bearer header, returns the standard
// `{ code, message, data, errors, meta }` envelope, scopes data to the caller's
// brand server-side (the FE never sends the brand), and 403s for any
// non-drive-thru user — callers should treat a 403 as "not a demo account" and
// route back to login.
import axios from "axios";
import { getCookie } from "@util/cookie";
import type {
    DriveThruConfig,
    DriveThruMenu,
    DriveThruOrder,
    DriveThruOrderPoll,
    DriveThruSignedUrl,
} from "@type/drive-thru";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

function unwrap<T>(payload: unknown): T {
    const p = payload as { data?: T } | T;
    if (p && typeof p === "object" && "data" in (p as Record<string, unknown>)) {
        return (p as { data: T }).data;
    }
    return p as T;
}

// Call once on page load — brand, languages, agent ids, menu summary.
export async function getDriveThruConfig(): Promise<DriveThruConfig> {
    const res = await axios.get(`${apiURL}/drive-thru/config`, {
        headers: authHeaders(),
    });
    return unwrap<DriveThruConfig>(res.data);
}

// Mints a SINGLE-USE ElevenLabs signed WSS URL (~15 min TTL). Mint a fresh one
// for every call attempt/retry — a used URL cannot be reconnected. No language
// param: there is a single bilingual agent per brand that mirrors the caller's
// language automatically.
export async function getDriveThruSignedUrl(): Promise<DriveThruSignedUrl> {
    const res = await axios.get(`${apiURL}/drive-thru/signed-url`, {
        headers: authHeaders(),
    });
    return unwrap<DriveThruSignedUrl>(res.data);
}

// The ~1s polling endpoint while a call is active. `status: "none"` can persist
// for the first ~5–10s (the agent syncs on the first item, not on greeting).
export async function getDriveThruOrder(
    elConversationId: string
): Promise<DriveThruOrderPoll> {
    const res = await axios.get(`${apiURL}/drive-thru/order`, {
        params: { el_conversation_id: elConversationId },
        headers: authHeaders(),
    });
    return unwrap<DriveThruOrderPoll>(res.data);
}

// Finalized order history for the brand (drafts never appear), newest first.
export async function getDriveThruOrders(limit = 20): Promise<DriveThruOrder[]> {
    const res = await axios.get(`${apiURL}/drive-thru/orders`, {
        params: { limit },
        headers: authHeaders(),
    });
    const data = unwrap<DriveThruOrder[] | { orders?: DriveThruOrder[] }>(res.data);
    return Array.isArray(data) ? data : (data.orders ?? []);
}

// Full structured menu for the brand (for a menu board / sold-out picker).
export async function getDriveThruMenu(): Promise<DriveThruMenu> {
    const res = await axios.get(`${apiURL}/drive-thru/menu`, {
        headers: authHeaders(),
    });
    return unwrap<DriveThruMenu>(res.data);
}

// Thrown when the sold-out update is rejected (code 400 — unknown item names).
// Nothing is saved in that case; `unknownItems` lists the offending names.
export class DriveThruSoldOutError extends Error {
    unknownItems: string[];
    constructor(message: string, unknownItems: string[]) {
        super(message);
        this.name = "DriveThruSoldOutError";
        this.unknownItems = unknownItems;
    }
}

interface SoldOutEnvelope {
    code?: number;
    message?: string;
    errors?: string[];
    data?: { sold_out?: string[] };
}

// Demo control: replace the FULL sold-out list (send [] to reset). The agent
// then refuses those items / suggests alternatives mid-demo. Any unknown item
// name fails the WHOLE update (code 400, unknown names in `errors`, nothing
// saved) — that failure surfaces as DriveThruSoldOutError whether the BE
// signals it via HTTP status or via the envelope `code`. On success returns
// the authoritative saved list from `data.sold_out`.
export async function setDriveThruSoldOut(soldOut: string[]): Promise<string[]> {
    let envelope: SoldOutEnvelope;
    try {
        const res = await axios.post(
            `${apiURL}/drive-thru/sold-out`,
            { sold_out: soldOut },
            { headers: authHeaders() }
        );
        envelope = res.data as SoldOutEnvelope;
    } catch (err) {
        const body = (err as { response?: { data?: SoldOutEnvelope } })?.response
            ?.data;
        if (body && (body.message || body.errors?.length)) {
            throw new DriveThruSoldOutError(
                body.message ?? "Sold-out update failed",
                body.errors ?? []
            );
        }
        throw err;
    }
    if (typeof envelope?.code === "number" && envelope.code !== 200) {
        throw new DriveThruSoldOutError(
            envelope.message ?? "Sold-out update failed",
            envelope.errors ?? []
        );
    }
    return envelope?.data?.sold_out ?? soldOut;
}
